import { useParams, Link } from "react-router-dom";

const SERVICE_CONTENT = {
  "data-automation": {
    title: "Data Automation",
    icon: "🤖",
    tagline: "Eliminate manual processes. Deliver real-time insights automatically.",
    overview: `Data automation is the backbone of modern business intelligence. We design and build automated pipelines that collect, clean, transform and deliver data without manual intervention — freeing your team to focus on decisions rather than data wrangling.`,
    capabilities: [
      "Automated ETL pipelines for real-time and batch data processing",
      "Scheduled data pulls from APIs, databases and flat files",
      "Alert systems triggered by thresholds or anomalies in your data",
      "Excel VBA macro automation for business reporting workflows",
      "Integration with third-party platforms including CRMs and ERPs",
      "Python-based automation scripts with logging and error handling",
    ],
    workSamples: [
      {
        title: "Inventory Stock Reorder System — Excel Automation Dashboard",
        description: `A practical Excel-based solution automating inventory monitoring, reorder filtering, conditional formatting, KPI insights, PDF generation and email dispatch. The system simplifies stock management using macros and interactive dashboards. It identified 595 reorder items out of 2,000 inventory lines, flagged top suppliers including Gulf Coast Traders (221 items) and Coastal Logistics Partners (212 items), and automated the full reorder reporting workflow including PDF export and Outlook email dispatch via VBA.`,
        tech: ["Microsoft Excel", "VBA", "Pivot Charts", "Outlook Automation", "GitHub"],
        image: null,
        links: [
          { label: "View on GitHub",   url: "https://github.com/FijabiAdekunle/Inventory-Stock-Reorder-System-" },
          { label: "View Portfolio",   url: "https://sites.google.com/view/fijabi-j-adekunle/inventory-stock-reorder-system" },
        ],
        highlights: [
          "595 reorder items identified from 2,000 inventory lines",
          "Top supplier reorder needs automatically flagged",
          "Reusable VBA macro module (.bas) exported for portability",
          "PDF export of filtered reorder sheet",
          "Automated email dispatch via Outlook VBA integration",
        ],
      },
      {
        title: "Martsense Inventory Automation",
        description: `Designed the automated inventory tracking engine inside Martsense that monitors stock levels in real-time, triggers low-stock alerts when products fall below defined thresholds and automatically logs every stock movement with timestamps for complete audit trail purposes.`,
        tech: ["FastAPI", "PostgreSQL", "Neon", "Python"],
        image: null,
        links: [
          { label: "Launch App", url: "https://.martsense.com.ng" },
        ],
        highlights: [],
      },
      {
        title: "Forex Strategy Alert System — Telegram Automation",
        useImageGrid: true,
        description: `Built a fully automated Forex strategy alert system that analyzes live market data across three currency pairs and Gold and delivers structured trading signals directly to a subscriber's Telegram channel. The system runs on a scheduled basis and pushes real-time alerts covering trend direction, price levels, RSI, EMA crossovers, ATR volatility range, support and resistance levels, candlestick patterns, market sentiment and a composite signal score out of 100. Live news headlines are also pulled and included in each alert for context.`,
        tech: ["Python", "Telegram Bot API", "REST APIs", "Technical Analysis", "Automation"],
        image:  "/fx-telegram-alerts-1.jpg",
        imageAlt:  "EUR/USD Forex Strategy Alert",
        image2: "/fx-telegram-alerts-2.jpg",
        image2Alt: "USD/JPY Forex Strategy Alert",
        image3: "/fx-telegram-alerts-3.jpg",
        image3Alt: "XAU/USD Forex Strategy Alert",
        image4: "/fx-telegram-alerts-4.jpg",
        image4Alt: "GBP/USD Forex Strategy Alert",
        links: [],
        highlights: [
          "Monitors EUR/USD, GBP/USD, USD/JPY and XAU/USD simultaneously",
          "Real-time RSI, EMA10 and EMA50 crossover analysis",
          "ATR volatility range and support/resistance level detection",
          "Composite signal score from 0 to 100 for quick decision making",
          "Live forex news headlines included in every alert",
          "Fully automated delivery to Telegram channel on schedule",
        ],
      },
    ],
  },

  "database-management": {
    title: "Database Management",
    icon: "🗄️",
    tagline: "Robust, scalable database systems built to grow with your business.",
    overview: `A poorly designed database is a ticking time bomb for any software product. We architect database systems from the ground up — designing schemas that are normalized, performant and future-proof. From initial design to migration, optimization and ongoing management, we treat your data infrastructure as a strategic asset.`,
    capabilities: [
      "Relational database schema design and normalization",
      "PostgreSQL setup and optimization",
      "Database migration planning and execution",
      "Query optimization and performance tuning",
      "Backup strategies and disaster recovery planning",
      "Cloud database setup on Neon, Supabase and AWS RDS",
    ],
    workSamples: [
      {
        title: "Martsense Database Architecture",
        description: `Designed and implemented a 9-table relational database schema for Martsense covering businesses, users, products, categories, stock movements, customers, sales, sale items and subscriptions. The schema supports multi-tenant isolation, cascading relationships and subscription-gated access control — deployed on Neon PostgreSQL with SSL encryption.`,
        tech: ["PostgreSQL", "Neon", "SQL", "Multi-tenancy"],
        image: "/schema-stocksense.png",
        imageAlt: "StockSense PostgreSQL Database Schema",
        links: [],
        highlights: [],
      },
      {
        title: "Forex Strategy Assistant Database",
        description: `Designed and built the database architecture for the Forex Strategy Assistant application — capturing market data, strategy configurations and performance metrics in a structured PostgreSQL schema optimized for time-series financial data queries.`,
        tech: ["Python", "PostgreSQL", "Neon", "Streamlit"],
        image: "/schema-forex.png",
        imageAlt: "Forex Strategy Assistant Database Schema",
        links: [],
        highlights: [],
      },
    ],
  },

  "data-analytics": {
    title: "Data Analytics & Visualization",
    icon: "📊",
    tagline: "Turn raw data into decisions your business can act on.",
    overview: `Data without insight is just noise. We transform your raw business data into clear, actionable intelligence — building dashboards, reports and analytical models that answer your most important business questions. Our visualizations are designed for clarity and built for the people who actually use them.`,
    capabilities: [
      "Interactive dashboard development with PowerBI, Tableau and Streamlit",
      "Sales performance and revenue trend analysis",
      "Customer behavior and churn analysis",
      "Operational efficiency and KPI tracking",
      "Streamlit-based web analytics applications",
      "Custom report generation in PDF and CSV formats",
    ],
    workSamples: [
      {
        title: "OceanGuard Analytics Platform",
        description: `Built a full Streamlit-based maritime accident prevention platform on MAIB accident data covering 2020–2024. The platform features KPI summary cards, interactive Plotly charts, sidebar filters by country, accident type and vessel category, and a multi-country architecture designed for future NIMASA, EMSA and USCG data integration.`,
        tech: ["Python", "Streamlit", "Plotly", "Pandas", "MAIB Data"],
        image: null,
        links: [
          { label: "View Streamlit Dashboard", url: "https://oceanguard-analytics-egg7zqwswgdmzrnybexkbj.streamlit.app/" },
        ],
        highlights: [],
      },
      {
        title: "Martsense Business Analytics Dashboard",
        description: `Designed and built the full analytics engine inside Martsense — including 30-day revenue trend charts, top selling product bar charts, daily and monthly profit calculations, low stock alert dashboards and CSV export for offline analysis. All charts are served in real-time from a FastAPI backend querying PostgreSQL.`,
        tech: ["React", "Recharts", "FastAPI", "PostgreSQL"],
        image:     "/dashboard-stocksense.png",
        imageAlt:  "StockSense Business Analytics Dashboard",
        image2:    "/line-charts-stocksense.png",
        image2Alt: "StockSense Revenue Trend Line Chart",
        image3:    "/Bar-charts-stocksense.png",
        image3Alt: "StockSense Top Products Bar Chart",
        links: [
          { label: "Launch App", url: "https://.martsense.com.ng" },
        ],
        highlights: [],
      },
    ],
  },

  "machine-learning": {
    title: "Machine Learning Solutions",
    icon: "🧠",
    tagline: "Predictive models grounded in domain knowledge and built for production.",
    overview: `Machine learning is only valuable when it solves real problems reliably. We build and deploy custom ML models for classification, prediction and anomaly detection — always starting from domain expertise rather than pure algorithmic experimentation. Every model we ship is explainable, measurable and production-ready.`,
    capabilities: [
      "Binary and multi-class classification models",
      "Predictive maintenance and failure forecasting",
      "Customer churn prediction and retention modeling",
      "Feature engineering and selection pipelines",
      "Model evaluation, validation and performance tracking",
      "Deployment via FastAPI REST endpoints",
    ],
    workSamples: [
      {
        title: "Bank Customer Churn ML Pipeline",
        description: `Built an end-to-end machine learning pipeline to predict bank customer churn. The pipeline covers exploratory data analysis, feature engineering, model training with multiple classifiers (Logistic Regression, Random Forest, XGBoost), hyperparameter tuning, cross-validation and final model evaluation. Published on Kaggle with full documentation.`,
        tech: ["Python", "Scikit-learn", "XGBoost", "Pandas", "Kaggle"],
        image: null,
        links: [
          { label: "View on Kaggle", url: "https://www.kaggle.com/competitions/bank-customer-churn-prediction-challenge" },
        ],
        highlights: [],
      },
      {
        title: "Ship Performance Clustering",
        description: `Applied unsupervised machine learning to cluster vessels by operational performance patterns using historical voyage data. The clustering model identified distinct performance groups enabling targeted maintenance scheduling and fuel optimization recommendations for fleet managers.`,
        tech: ["Python", "Scikit-learn", "K-Means", "Matplotlib"],
        image: null,
        links: [
          { label: "View on GitHub", url: "https://github.com/FijabiAdekunle/Ship-Performance-Clustering" },
        ],
        highlights: [],
      },
    ],
  },

  "custom-software": {
    title: "Custom Software Development",
    icon: "📦",
    tagline: "From idea to production-ready SaaS — built to last.",
    overview: `We build full-stack web applications and SaaS products tailored to your specific business requirements. Every line of code is written with scalability, security and maintainability in mind. We handle everything from database architecture to frontend design to deployment and ongoing support.`,
    capabilities: [
      "Full-stack web application development with React and FastAPI",
      "SaaS product architecture with subscription and billing integration",
      "REST API design and development",
      "Payment integration with Paystack",
      "PWA development for mobile-first experiences",
      "Cloud deployment on Vercel, Render and AWS",
    ],
    workSamples: [
      {
        title: "Martsense — Inventory SaaS",
        description: `Built Martsense from zero to production — a complete multi-tenant SaaS inventory and sales management platform for African SMEs. The product features a React PWA frontend, FastAPI backend, PostgreSQL on Neon, Paystack subscription billing, role-based access control, CSV bulk import, real-time analytics and a full admin dashboard. Currently live and accepting paying subscribers.`,
        tech: ["React", "FastAPI", "PostgreSQL", "Paystack", "Vercel", "Render"],
        image: null,
        links: [
          { label: "Launch App", url: "https://.martsense.com.ng" },
        ],
        highlights: [],
      },
      {
        title: "Forex Strategy Assistant",
        description: `Built an AI-powered forex strategy assistant using Python and Streamlit. The application provides real-time market analysis, strategy recommendations and performance tracking for forex traders. Deployed as a live web application accessible from any device.`,
        tech: ["Python", "Streamlit", "AI", "REST APIs"],
        image: null,
        links: [
          { label: "Launch App", url: "https://forexstrategy.streamlit.app/" },
        ],
        highlights: [],
      },
    ],
  },

  "maritime-technology": {
    title: "Maritime Technology Consulting",
    icon: "🚢",
    tagline: "Nearly two decades of marine engineering expertise — now powered by data.",
    heroImg: "/maritime-hero.webp",
    overview: `Few technology consultants understand the maritime and offshore industry from the inside. With nearly two decades of hands-on experience as a Marine Engineer on vessels and offshore platforms, we bring a rare combination of operational domain knowledge and modern software capability to maritime technology projects.`,
    capabilities: [
      "Maritime accident analysis and prevention systems",
      "Offshore inventory and logistics software",
      "Vessel performance monitoring and analytics",
      "Regulatory compliance tracking systems",
      "Crew management and scheduling tools",
      "Integration with NIMASA, EMSA and USCG data sources",
    ],
    workSamples: [
      {
        title: "OceanGuard Analytics",
        description: `Designed and built a maritime accident prevention analytics platform using 5 years of MAIB accident investigation data. The platform provides fleet operators and maritime safety officers with interactive visualizations of accident trends, high-risk vessel types, environmental conditions and causal factors — enabling evidence-based safety interventions.`,
        tech: ["Python", "Streamlit", "Plotly", "MAIB Data", "Pandas"],
        image: null,
        links: [
          { label: "View Live App", url: "https://oceanguard-analytics-egg7zqwswgdmzrnybexkbj.streamlit.app/" },
        ],
        highlights: [],
      },
      {
        title: "VesselStock Pro (In Development)",
        description: `Currently building VesselStock Pro — a comprehensive inventory management system purpose-built for ships, offshore platforms and marine operations. The system will track spare parts, consumables, safety equipment and provisions with full audit trails, reorder automation and compliance reporting.`,
        tech: ["React", "FastAPI", "PostgreSQL", "Maritime Standards"],
        image: null,
        links: [],
        highlights: [],
      },
    ],
  },
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICE_CONTENT[slug];

  if (!service) {
    return (
      <div style={s.notFound}>
        <h1 style={s.notFoundTitle}>Service not found</h1>
        <Link to="/services" style={s.backBtn}>← Back to Services</Link>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.inner}>
        <Link to="/services" style={s.backLink}>← Back to Services</Link>

        <div style={s.hero}>
          {service.heroImg && (
            <div style={{
              width: "100%", borderRadius: "14px",
              overflow: "hidden", marginBottom: "28px",
              height: "200px", position: "relative",
            }}>
              <img
                src={service.heroImg}
                alt={service.title}
                style={{
                  width: "100%", height: "100%",
                  objectFit: "cover", objectPosition: "center",
                  opacity: 0.5,
                }}
              />
              <div style={{
                position: "absolute", inset: 0,
                background: "linear-gradient(135deg, rgba(4,10,10,0.85) 0%, rgba(46,196,182,0.06) 100%)",
              }} />
            </div>
          )}
          <span style={s.heroIcon}>{service.icon}</span>
          <div style={s.sectionTag}>Our Services</div>
          <h1 style={s.heroTitle}>{service.title}</h1>
          <p style={s.heroTagline}>{service.tagline}</p>
        </div>

        <div style={s.overview}>
          <p style={s.overviewText}>{service.overview}</p>
        </div>

        <div style={s.capabilitiesSection}>
          <h2 style={s.sectionTitle}>What We Deliver</h2>
          <div style={s.capabilitiesGrid}>
            {service.capabilities.map((cap, i) => (
              <div key={i} style={s.capabilityItem}>
                <span style={s.capabilityCheck}>✓</span>
                <span style={s.capabilityText}>{cap}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={s.samplesSection}>
          <h2 style={s.sectionTitle}>Work Samples</h2>
          <div style={s.samplesGrid}>
            {service.workSamples.map((sample, i) => (
              <div key={i} style={s.sampleCard}>
                <h3 style={s.sampleTitle}>{sample.title}</h3>
                <p style={s.sampleDesc}>{sample.description}</p>

                {sample.highlights && sample.highlights.length > 0 && (
                  <div style={s.highlights}>
                    {sample.highlights.map((h, j) => (
                      <div key={j} style={s.highlightItem}>
                        <span style={s.highlightCheck}>✓</span>
                        <span style={s.highlightText}>{h}</span>
                      </div>
                    ))}
                  </div>
                )}

                {sample.useImageGrid && sample.image ? (
                  <div style={s.imageGrid}>
                    {sample.image  && <img src={sample.image}  alt={sample.imageAlt}  style={s.sampleImageGrid} />}
                    {sample.image2 && <img src={sample.image2} alt={sample.image2Alt} style={s.sampleImageGrid} />}
                    {sample.image3 && <img src={sample.image3} alt={sample.image3Alt} style={s.sampleImageGrid} />}
                    {sample.image4 && <img src={sample.image4} alt={sample.image4Alt} style={s.sampleImageGrid} />}
                  </div>
                ) : (
                  <>
                    {sample.image  && <img src={sample.image}  alt={sample.imageAlt  || sample.title} style={s.sampleImage} />}
                    {sample.image2 && <img src={sample.image2} alt={sample.image2Alt || sample.title} style={s.sampleImage} />}
                    {sample.image3 && <img src={sample.image3} alt={sample.image3Alt || sample.title} style={s.sampleImage} />}
                    {sample.image4 && <img src={sample.image4} alt={sample.image4Alt || sample.title} style={s.sampleImage} />}
                  </>
                )}

                <div style={s.sampleTech}>
                  {sample.tech.map((t) => (
                    <span key={t} style={s.techBadge}>{t}</span>
                  ))}
                </div>

                {sample.links && sample.links.length > 0 && (
                  <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                    {sample.links.map((link, k) => (
                      <a
                        key={k}
                        href={link.url}
                        target="_blank"
                        rel="noreferrer"
                        style={k === 0 ? s.sampleLink : s.sampleLinkSecondary}
                      >
                        {link.label} →
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div style={s.cta}>
          <h2 style={s.ctaTitle}>Ready to work together?</h2>
          <p style={s.ctaText}>
            Tell us about your project and we'll get back to you within 24 hours.
          </p>
          <div style={s.ctaBtns}>
            <a href="https://wa.me/2348077460114" target="_blank" rel="noreferrer" style={s.ctaBtnPrimary}>
              💬 WhatsApp Us
            </a>
            <a href="mailto:stacksensetechnologies@gmail.com" style={s.ctaBtnSecondary}>
              📧 Email Us
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

const s = {
  page:  { background: "#070d0d", minHeight: "100vh", padding: "80px 24px" },
  inner: { maxWidth: "900px", margin: "0 auto" },
  backLink: {
    display: "inline-block", color: "#567878",
    textDecoration: "none", fontSize: "14px",
    marginBottom: "40px", fontWeight: 500,
  },

  hero:       { marginBottom: "48px" },
  heroIcon:   { fontSize: "52px", display: "block", marginBottom: "16px" },
  sectionTag: {
    display: "inline-block",
    background: "rgba(46,196,182,0.08)", border: "1px solid rgba(46,196,182,0.18)",
    borderRadius: "20px", padding: "4px 14px",
    fontSize: "11px", color: "#2ec4b6", marginBottom: "16px",
    textTransform: "uppercase", letterSpacing: "1.2px", fontWeight: 600,
  },
  heroTitle: {
    fontSize: "clamp(30px, 6vw, 52px)", fontWeight: 800,
    color: "#e8f5f5", marginBottom: "14px", lineHeight: "1.1",
    letterSpacing: "-1.5px",
    fontFamily: "'Space Grotesk', 'DM Sans', sans-serif",
  },
  heroTagline: { fontSize: "17px", color: "#2ec4b6", fontWeight: 600 },

  overview: {
    background: "#0c1515", border: "1px solid #182828",
    borderRadius: "14px", padding: "32px", marginBottom: "48px",
  },
  overviewText: { fontSize: "17px", color: "#8ab0b0", lineHeight: "1.85" },

  capabilitiesSection: { marginBottom: "48px" },
  sectionTitle: {
    fontSize: "22px", fontWeight: 700, color: "#e8f5f5",
    marginBottom: "24px", letterSpacing: "-0.5px",
    fontFamily: "'Space Grotesk', 'DM Sans', sans-serif",
  },
  capabilitiesGrid: { display: "flex", flexDirection: "column", gap: "10px" },
  capabilityItem: {
    display: "flex", alignItems: "flex-start", gap: "12px",
    background: "#0c1515", border: "1px solid #182828",
    borderRadius: "10px", padding: "15px",
  },
  capabilityCheck: { color: "#2ec4b6", fontWeight: 700, flexShrink: 0 },
  capabilityText:  { fontSize: "15px", color: "#8ab0b0", lineHeight: "1.65" },

  samplesSection: { marginBottom: "48px" },
  samplesGrid:    { display: "flex", flexDirection: "column", gap: "24px" },
  sampleCard: {
    background: "#0c1515", border: "1px solid #182828",
    borderRadius: "16px", padding: "32px",
  },
  sampleTitle: {
    fontSize: "19px", fontWeight: 700, color: "#e8f5f5",
    marginBottom: "12px",
    fontFamily: "'Space Grotesk', 'DM Sans', sans-serif",
  },
  sampleDesc: { fontSize: "15px", color: "#8ab0b0", lineHeight: "1.8", marginBottom: "16px" },
  sampleTech: { display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "16px" },
  techBadge: {
    background: "#121f1f", color: "#2ec4b6",
    borderRadius: "6px", padding: "4px 10px",
    fontSize: "11px", fontWeight: 600,
  },
  sampleLink: {
    display: "inline-block", color: "#2ec4b6",
    textDecoration: "none", fontSize: "14px", fontWeight: 700,
  },
  sampleLinkSecondary: {
    display: "inline-block", color: "#567878",
    textDecoration: "none", fontSize: "14px", fontWeight: 600,
    borderBottom: "1px solid #182828", paddingBottom: "2px",
  },
  sampleImage: {
    width: "100%", borderRadius: "10px",
    border: "1px solid #182828", marginBottom: "12px", display: "block",
  },
  imageGrid: {
    display: "flex", flexDirection: "row",
    gap: "10px", marginBottom: "16px", overflowX: "auto",
  },
  sampleImageGrid: {
    height: "300px", width: "auto",
    borderRadius: "10px", border: "1px solid #182828",
    display: "block", objectFit: "contain", flexShrink: 0,
  },
  highlights: {
    display: "flex", flexDirection: "column", gap: "8px",
    marginBottom: "20px", background: "#0a1e1e",
    borderRadius: "10px", padding: "16px",
  },
  highlightItem:  { display: "flex", alignItems: "flex-start", gap: "10px" },
  highlightCheck: { color: "#2ec4b6", fontWeight: 700, flexShrink: 0 },
  highlightText:  { fontSize: "14px", color: "#8ab0b0", lineHeight: "1.6" },

  cta: {
    background: "linear-gradient(135deg, #0a1e1e, #0c1515)",
    border: "1px solid rgba(46,196,182,0.2)",
    borderRadius: "16px", padding: "48px 32px", textAlign: "center",
  },
  ctaTitle: {
    fontSize: "26px", fontWeight: 800, color: "#e8f5f5",
    marginBottom: "12px",
    fontFamily: "'Space Grotesk', 'DM Sans', sans-serif",
  },
  ctaText:  { fontSize: "16px", color: "#8ab0b0", marginBottom: "28px" },
  ctaBtns:  { display: "flex", gap: "14px", justifyContent: "center", flexWrap: "wrap" },
  ctaBtnPrimary: {
    background: "#2ec4b6",
    color: "#040b0b", borderRadius: "10px",
    padding: "14px 28px", fontSize: "14px",
    fontWeight: 700, textDecoration: "none",
  },
  ctaBtnSecondary: {
    background: "none", border: "1px solid rgba(46,196,182,0.3)",
    color: "#2ec4b6", borderRadius: "10px",
    padding: "14px 28px", fontSize: "14px",
    fontWeight: 700, textDecoration: "none",
  },

  notFound: {
    minHeight: "100vh", display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center", background: "#070d0d",
  },
  notFoundTitle: { fontSize: "22px", color: "#e8f5f5", marginBottom: "24px" },
  backBtn:       { color: "#2ec4b6", textDecoration: "none", fontWeight: 700 },
};
